/**
 * WHO Child Growth Standards
 *
 * Data from the WHO Multicentre Growth Reference Study (MGRS)
 * https://www.who.int/tools/child-growth-standards/standards
 *
 * Contains percentile data for:
 * - Weight-for-age (0-24 months)
 * - Length/Height-for-age (0-24 months)
 * - Head circumference-for-age (0-24 months)
 *
 * Separated by sex (male/female)
 * Percentiles: 3rd, 15th, 50th, 85th, 97th
 */

export type Gender = 'male' | 'female';
export type MeasurementType = 'weight' | 'height' | 'head';

// Data point structure: [ageInDays, p3, p15, p50, p85, p97]
export type PercentileDataPoint = [number, number, number, number, number, number];

// ============================================================================
// WEIGHT-FOR-AGE (grams)
// ============================================================================

// Male weight data: [ageInDays, p3, p15, p50, p85, p97]
export const MALE_WEIGHT: PercentileDataPoint[] = [
  [0, 2500, 2900, 3300, 3700, 4200],
  [7, 2700, 3100, 3600, 4100, 4600],
  [14, 2900, 3400, 3900, 4500, 5100],
  [30, 3400, 3900, 4500, 5200, 5900],
  [60, 4300, 4900, 5600, 6400, 7200],
  [91, 5000, 5700, 6400, 7300, 8100],
  [122, 5600, 6300, 7000, 7900, 8700],
  [152, 6000, 6800, 7500, 8400, 9300],
  [182, 6400, 7200, 7900, 8800, 9700],
  [213, 6700, 7500, 8300, 9200, 10100],
  [244, 6900, 7800, 8600, 9500, 10500],
  [274, 7100, 8000, 8900, 9800, 10800],
  [305, 7300, 8200, 9100, 10100, 11100],
  [335, 7500, 8400, 9400, 10400, 11400],
  [365, 7700, 8600, 9600, 10600, 11700],
  [456, 8200, 9200, 10300, 11400, 12600],
  [548, 8700, 9800, 10900, 12100, 13400],
  [639, 9200, 10300, 11500, 12800, 14100],
  [730, 9700, 10800, 12100, 13500, 14900],
];

// Female weight data: [ageInDays, p3, p15, p50, p85, p97]
export const FEMALE_WEIGHT: PercentileDataPoint[] = [
  [0, 2400, 2800, 3200, 3600, 4000],
  [7, 2600, 3000, 3400, 3900, 4400],
  [14, 2800, 3200, 3700, 4200, 4800],
  [30, 3200, 3700, 4200, 4800, 5400],
  [60, 4000, 4500, 5100, 5900, 6600],
  [91, 4600, 5200, 5800, 6600, 7500],
  [122, 5100, 5800, 6400, 7300, 8100],
  [152, 5500, 6200, 6900, 7800, 8700],
  [182, 5900, 6600, 7300, 8200, 9100],
  [213, 6200, 6900, 7600, 8600, 9500],
  [244, 6400, 7200, 7900, 8900, 9900],
  [274, 6600, 7400, 8200, 9200, 10200],
  [305, 6800, 7600, 8500, 9500, 10500],
  [335, 7000, 7900, 8700, 9800, 10800],
  [365, 7200, 8100, 9000, 10000, 11100],
  [456, 7700, 8700, 9700, 10800, 12000],
  [548, 8200, 9200, 10300, 11500, 12800],
  [639, 8700, 9700, 10900, 12200, 13500],
  [730, 9100, 10200, 11500, 12900, 14300],
];

// ============================================================================
// LENGTH/HEIGHT-FOR-AGE (cm)
// ============================================================================

// Male height data: [ageInDays, p3, p15, p50, p85, p97]
export const MALE_HEIGHT: PercentileDataPoint[] = [
  [0, 46.3, 48.0, 49.9, 51.8, 53.4],
  [7, 47.5, 49.2, 51.1, 53.0, 54.7],
  [14, 48.6, 50.3, 52.3, 54.2, 55.9],
  [30, 50.8, 52.5, 54.7, 56.7, 58.6],
  [60, 54.7, 56.5, 58.4, 60.4, 62.2],
  [91, 57.6, 59.5, 61.4, 63.4, 65.3],
  [122, 60.0, 61.9, 63.9, 65.9, 67.8],
  [152, 62.0, 64.0, 65.9, 67.9, 69.9],
  [182, 63.8, 65.7, 67.6, 69.7, 71.6],
  [213, 65.4, 67.3, 69.2, 71.2, 73.2],
  [244, 66.8, 68.7, 70.6, 72.6, 74.6],
  [274, 68.1, 70.0, 71.9, 73.9, 75.9],
  [305, 69.4, 71.2, 73.2, 75.2, 77.1],
  [335, 70.5, 72.4, 74.4, 76.4, 78.3],
  [365, 71.6, 73.5, 75.5, 77.5, 79.4],
  [456, 74.5, 76.5, 78.6, 80.7, 82.7],
  [548, 77.0, 79.1, 81.3, 83.5, 85.6],
  [639, 79.2, 81.4, 83.7, 86.0, 88.2],
  [730, 81.3, 83.5, 85.9, 88.3, 90.5],
];

// Female height data: [ageInDays, p3, p15, p50, p85, p97]
export const FEMALE_HEIGHT: PercentileDataPoint[] = [
  [0, 45.6, 47.2, 49.1, 50.9, 52.5],
  [7, 46.7, 48.4, 50.3, 52.1, 53.8],
  [14, 47.9, 49.5, 51.4, 53.3, 55.0],
  [30, 50.0, 51.7, 53.7, 55.6, 57.4],
  [60, 53.5, 55.3, 57.1, 59.0, 60.8],
  [91, 56.3, 58.1, 60.0, 61.9, 63.7],
  [122, 58.6, 60.4, 62.3, 64.2, 66.1],
  [152, 60.5, 62.4, 64.3, 66.2, 68.1],
  [182, 62.2, 64.1, 66.0, 67.9, 69.8],
  [213, 63.7, 65.6, 67.5, 69.5, 71.4],
  [244, 65.1, 67.0, 68.9, 70.9, 72.8],
  [274, 66.4, 68.3, 70.2, 72.2, 74.1],
  [305, 67.6, 69.5, 71.5, 73.5, 75.4],
  [335, 68.8, 70.7, 72.7, 74.7, 76.6],
  [365, 69.9, 71.8, 73.9, 75.9, 77.8],
  [456, 72.8, 74.9, 77.0, 79.1, 81.1],
  [548, 75.3, 77.5, 79.7, 82.0, 84.1],
  [639, 77.6, 79.9, 82.2, 84.5, 86.7],
  [730, 79.6, 82.0, 84.4, 86.8, 89.1],
];

// ============================================================================
// HEAD CIRCUMFERENCE-FOR-AGE (cm)
// ============================================================================

// Male head circumference data: [ageInDays, p3, p15, p50, p85, p97]
export const MALE_HEAD: PercentileDataPoint[] = [
  [0, 32.4, 33.4, 34.5, 35.6, 36.6],
  [7, 33.1, 34.1, 35.2, 36.3, 37.4],
  [14, 33.8, 34.8, 35.9, 37.0, 38.1],
  [30, 35.1, 36.1, 37.3, 38.4, 39.5],
  [60, 37.0, 38.1, 39.1, 40.2, 41.3],
  [91, 38.3, 39.4, 40.5, 41.6, 42.7],
  [122, 39.4, 40.4, 41.6, 42.7, 43.8],
  [152, 40.2, 41.3, 42.4, 43.6, 44.7],
  [182, 40.9, 42.0, 43.2, 44.3, 45.4],
  [213, 41.5, 42.6, 43.8, 44.9, 46.0],
  [244, 42.0, 43.1, 44.3, 45.4, 46.5],
  [274, 42.4, 43.5, 44.7, 45.9, 47.0],
  [305, 42.8, 43.9, 45.1, 46.3, 47.4],
  [335, 43.1, 44.3, 45.5, 46.6, 47.8],
  [365, 43.4, 44.6, 45.8, 47.0, 48.1],
  [456, 44.2, 45.4, 46.6, 47.8, 49.0],
  [548, 44.8, 46.0, 47.3, 48.5, 49.7],
  [639, 45.3, 46.5, 47.8, 49.0, 50.2],
  [730, 45.7, 46.9, 48.2, 49.5, 50.7],
];

// Female head circumference data: [ageInDays, p3, p15, p50, p85, p97]
export const FEMALE_HEAD: PercentileDataPoint[] = [
  [0, 31.9, 32.8, 33.9, 34.9, 35.9],
  [7, 32.5, 33.5, 34.5, 35.6, 36.6],
  [14, 33.2, 34.2, 35.2, 36.3, 37.3],
  [30, 34.4, 35.4, 36.5, 37.6, 38.6],
  [60, 36.0, 37.1, 38.3, 39.4, 40.5],
  [91, 37.3, 38.4, 39.5, 40.7, 41.8],
  [122, 38.3, 39.4, 40.5, 41.7, 42.8],
  [152, 39.0, 40.2, 41.3, 42.5, 43.6],
  [182, 39.7, 40.8, 42.0, 43.2, 44.3],
  [213, 40.2, 41.4, 42.5, 43.7, 44.8],
  [244, 40.7, 41.8, 43.0, 44.2, 45.3],
  [274, 41.1, 42.2, 43.4, 44.6, 45.7],
  [305, 41.4, 42.6, 43.8, 45.0, 46.1],
  [335, 41.7, 42.9, 44.1, 45.3, 46.4],
  [365, 42.0, 43.2, 44.4, 45.6, 46.7],
  [456, 42.7, 43.9, 45.2, 46.4, 47.6],
  [548, 43.3, 44.5, 45.8, 47.0, 48.2],
  [639, 43.8, 45.0, 46.3, 47.5, 48.7],
  [730, 44.2, 45.4, 46.7, 48.0, 49.2],
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the appropriate dataset based on gender and measurement type
 */
export function getDataset(gender: Gender, type: MeasurementType): PercentileDataPoint[] {
  const datasets: Record<Gender, Record<MeasurementType, PercentileDataPoint[]>> = {
    male: {
      weight: MALE_WEIGHT,
      height: MALE_HEIGHT,
      head: MALE_HEAD,
    },
    female: {
      weight: FEMALE_WEIGHT,
      height: FEMALE_HEIGHT,
      head: FEMALE_HEAD,
    },
  };
  return datasets[gender][type];
}

/**
 * Interpolate between two data points
 */
function interpolate(
  ageInDays: number,
  lower: PercentileDataPoint,
  upper: PercentileDataPoint
): PercentileDataPoint {
  const [lowerAge, ...lowerValues] = lower;
  const [upperAge, ...upperValues] = upper;

  const ratio = (ageInDays - lowerAge) / (upperAge - lowerAge);

  const interpolatedValues = lowerValues.map((lv, i) => {
    const uv = upperValues[i];
    return lv + (uv - lv) * ratio;
  });

  return [ageInDays, ...interpolatedValues] as PercentileDataPoint;
}

/**
 * Get percentile values for a specific age
 */
export function getPercentilesForAge(
  ageInDays: number,
  gender: Gender,
  type: MeasurementType
): { p3: number; p15: number; p50: number; p85: number; p97: number } {
  const dataset = getDataset(gender, type);

  // Clamp age to valid range
  const clampedAge = Math.max(0, Math.min(730, ageInDays));

  // Find surrounding data points
  let lower = dataset[0];
  let upper = dataset[dataset.length - 1];

  for (let i = 0; i < dataset.length - 1; i++) {
    if (dataset[i][0] <= clampedAge && dataset[i + 1][0] >= clampedAge) {
      lower = dataset[i];
      upper = dataset[i + 1];
      break;
    }
  }

  // Interpolate if needed
  const interpolated = lower[0] === upper[0]
    ? lower
    : interpolate(clampedAge, lower, upper);

  return {
    p3: interpolated[1],
    p15: interpolated[2],
    p50: interpolated[3],
    p85: interpolated[4],
    p97: interpolated[5],
  };
}

/**
 * Calculate the approximate percentile for a given value
 *
 * Uses linear interpolation between known percentile boundaries.
 * Returns a value between 0 and 100.
 */
export function calculatePercentile(
  value: number,
  ageInDays: number,
  type: MeasurementType,
  gender: Gender
): number {
  const percentiles = getPercentilesForAge(ageInDays, gender, type);

  // Define percentile boundaries
  const boundaries: [number, number][] = [
    [percentiles.p3, 3],
    [percentiles.p15, 15],
    [percentiles.p50, 50],
    [percentiles.p85, 85],
    [percentiles.p97, 97],
  ];

  // Below 3rd percentile
  if (value <= percentiles.p3) {
    // Extrapolate below - rough estimate
    const ratio = value / percentiles.p3;
    return Math.max(0, Math.round(3 * ratio));
  }

  // Above 97th percentile
  if (value >= percentiles.p97) {
    // Extrapolate above - rough estimate
    const excess = value - percentiles.p97;
    const range = percentiles.p97 - percentiles.p85;
    const additionalPercentile = range > 0 ? (excess / range) * 3 : 0;
    return Math.min(100, Math.round(97 + additionalPercentile));
  }

  // Interpolate between known percentiles
  for (let i = 0; i < boundaries.length - 1; i++) {
    const [lowerValue, lowerPercentile] = boundaries[i];
    const [upperValue, upperPercentile] = boundaries[i + 1];

    if (value >= lowerValue && value <= upperValue) {
      const ratio = (value - lowerValue) / (upperValue - lowerValue);
      return Math.round(lowerPercentile + ratio * (upperPercentile - lowerPercentile));
    }
  }

  return 50; // Fallback
}

/**
 * Get percentile label with description
 */
export function getPercentileLabel(percentile: number): {
  label: string;
  color: string;
  description: string;
} {
  if (percentile < 3) {
    return {
      label: `P${percentile}`,
      color: 'text-red-400',
      description: 'Deutlich unter dem Durchschnitt',
    };
  }
  if (percentile < 15) {
    return {
      label: `P${percentile}`,
      color: 'text-amber-400',
      description: 'Unter dem Durchschnitt',
    };
  }
  if (percentile < 85) {
    return {
      label: `P${percentile}`,
      color: 'text-emerald-400',
      description: 'Im Normalbereich',
    };
  }
  if (percentile < 97) {
    return {
      label: `P${percentile}`,
      color: 'text-amber-400',
      description: 'Über dem Durchschnitt',
    };
  }
  return {
    label: `P${percentile}`,
    color: 'text-red-400',
    description: 'Deutlich über dem Durchschnitt',
  };
}

/**
 * Format age for display
 */
export function formatAge(ageInDays: number): string {
  if (ageInDays === 0) return 'Geburt';
  if (ageInDays < 7) return `${ageInDays} ${ageInDays === 1 ? 'Tag' : 'Tage'}`;
  if (ageInDays < 30) {
    const weeks = Math.floor(ageInDays / 7);
    return `W${weeks}`;
  }
  const months = Math.floor(ageInDays / 30.44);
  return `M${months}`;
}

/**
 * Get chart data for rendering percentile curves
 */
export function getChartData(
  gender: Gender,
  type: MeasurementType,
  maxAgeInDays: number = 365
): {
  agePoints: number[];
  p3: number[];
  p15: number[];
  p50: number[];
  p85: number[];
  p97: number[];
} {
  const dataset = getDataset(gender, type);

  // Filter to max age and interpolate for smooth curves
  const relevantData = dataset.filter(d => d[0] <= maxAgeInDays);

  return {
    agePoints: relevantData.map(d => d[0]),
    p3: relevantData.map(d => d[1]),
    p15: relevantData.map(d => d[2]),
    p50: relevantData.map(d => d[3]),
    p85: relevantData.map(d => d[4]),
    p97: relevantData.map(d => d[5]),
  };
}

/**
 * Get measurement unit and label
 */
export function getMeasurementInfo(type: MeasurementType): {
  label: string;
  unit: string;
  shortLabel: string;
} {
  switch (type) {
    case 'weight':
      return { label: 'Gewicht', unit: 'g', shortLabel: 'Gew.' };
    case 'height':
      return { label: 'Größe', unit: 'cm', shortLabel: 'Gr.' };
    case 'head':
      return { label: 'Kopfumfang', unit: 'cm', shortLabel: 'KU' };
  }
}
